import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "../../../shared/components/BaseInput";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import {
  useNAMStates,
  useStateByAbbr,
} from "pages/admin-app/states/hooks/useNAMStates";
import {
  DistributorFormData,
  getDistributorFormSchema,
} from "./edit-distributor.schema";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { loadStates } from "pages/admin-app/states/services/loadStates";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";

interface Props {
  distributor: DistributorFormData & {
    country?: { id: number; name: string; abbr: string };
  };
  onSubmit: (data: DistributorFormData) => void;
  isSubmitting: boolean;
}

const EditDistributorForm = ({
  distributor,
  onSubmit,
  isSubmitting,
}: Props) => {
  const intl = useIntl();
  const distributorFormSchema = getDistributorFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(distributorFormSchema),
    defaultValues: distributor,
  });
  const { handleSubmit, control } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  const {
    data: states,
    isLoading: isLoadingStates,
    isError: isErrorStates,
    error: statesError,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const { data: selectedState } = useStateByAbbr({
    countryId: selectedCountry,
    abbr: distributor?.state,
  });

  const initialStatesDropdownValue = selectedState
    ? {
        value: selectedState.abbr,
        label: `${selectedState.name} (${selectedState.abbr})`,
      }
    : null;

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  const initialCountryDropdownValue =
    distributor.country?.id && distributor.country?.name
      ? {
          value: distributor.country.id,
          label: distributor.country.name,
        }
      : null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.distributor" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <BaseInput
          name="email"
          label={intl.formatMessage({ id: "usAdmin.email.distributor" })}
          type="text"
          containerClassName="usadmin-email"
        />
        <BaseInput
          name="phone"
          label={intl.formatMessage({ id: "usAdmin.phone" })}
          type="text"
          containerClassName="usadmin-phone"
        />
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            initialResolvedOption={initialStatesDropdownValue}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            additional={{ page: 1 }}
            selectedCountry={selectedCountry}
            isDisabled={!selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            initialResolvedOption={initialCountryDropdownValue}
            optionMapper={(c) => ({
              value: c.id,
              label: c.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectCountry",
            })}
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.update" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => {}}
        />
      </form>
    </FormProvider>
  );
};

export default EditDistributorForm;
